import { mdiBugOutline, mdiAutoFix } from '@mdi/js'
import type { OrgPermission, PublicFlag } from '@/types/permissions'

import dashboardIcon from '@/images/icons/apps/dashboard.svg?raw'
import mediasboxIcon from '@/images/icons/apps/mediasbox.svg?raw'
import scribeIcon from '@/images/icons/apps/scribe.svg?raw'
import clipsIcon from '@/images/icons/apps/clips.svg?raw'
import channelsIcon from '@/images/icons/apps/channels.svg?raw'
import analyticsIcon from '@/images/icons/apps/analytics.svg?raw'

/** One of the application's apps: an entry of the navigation rail, a card of the home page. */
export interface App {
  id: string
  titleKey: string
  descriptionKey: string
  icon: string
  color: string
  route?: string
  disabled?: boolean
  isOnMenu: boolean
  showOnHomepage?: boolean
  /** A public platform flag the app is behind: hidden while `GET /api/app/init` does not list it. */
  requiredFeature?: PublicFlag
  /**
   * A public flag the app is behind in the **active organization** (`GET /api/app/access`'s
   * `org.features`): hidden while that organization does not carry it.
   */
  requiredOrgFeature?: PublicFlag
  /** An organization permission the app is behind: hidden from whoever does not hold it. */
  requiredPermission?: OrgPermission
}

/** A quick action of the user drawer: a route to open, or an action the shell handles. */
export interface QuickAction {
  id: string
  icon: string
  label: string
  tooltip: string
  route?: string
  action?: string
  category?: string
  badge?: number | string
  badgeColor?: string
}

const videoGeneratorIcon = `<svg viewBox="0 0 24 24" fill="currentColor"><path d="${mdiAutoFix}" /></svg>`

/**
 * The application's apps, in the order the rail (`AppNavigationRail`) and the home page
 * (`AppsSection`) list them. `isOnMenu` and `showOnHomepage` say where each one appears; the
 * helpers below drop an app whose `requiredFeature` is off, and each reader drops one whose
 * `requiredPermission` the caller does not hold.
 */
export const shellApps: App[] = [
  {
    id: 'home',
    titleKey: 'home.app.title',
    descriptionKey: 'home.app.description',
    icon: dashboardIcon,
    color: '#1976D2',
    route: 'home',
    isOnMenu: true,
    showOnHomepage: false,
  },
  {
    id: 'assets',
    titleKey: 'assets.app.title',
    descriptionKey: 'assets.app.description',
    icon: mediasboxIcon,
    color: '#884AFF',
    route: 'assets',
    isOnMenu: true,
    showOnHomepage: true,
  },
  {
    id: 'scribe',
    titleKey: 'subtitles.app.title',
    descriptionKey: 'subtitles.app.description',
    icon: scribeIcon,
    color: '#22C55E',
    route: 'subtitles',
    isOnMenu: true,
    showOnHomepage: true,
    requiredPermission: 'subtitles.scribe.access',
  },
  // Channels is back on the rail and the homepage now that every screen of it, the editor's
  // canvas included, is served (docs/plans/frontend-websites.md WD4) — behind the platform's
  // `websites.enabled`, and shown to whoever may list an organization's channels, which a plain
  // member may not.
  {
    id: 'channels',
    titleKey: 'websites.app.title',
    descriptionKey: 'websites.app.description',
    icon: channelsIcon,
    color: '#FF58D3',
    route: 'websites',
    isOnMenu: true,
    showOnHomepage: true,
    requiredFeature: 'websites.enabled',
    requiredPermission: 'websites.website.manage',
  },
  // Clips is per organization: the rail and the homepage show it where `clips.enabled` is on.
  {
    id: 'clips',
    titleKey: 'clips.app.title',
    descriptionKey: 'clips.app.description',
    icon: clipsIcon,
    color: '#14B8A6',
    route: 'clips',
    isOnMenu: true,
    showOnHomepage: true,
    requiredOrgFeature: 'clips.enabled',
  },
  {
    id: 'analytics',
    titleKey: 'analytics.app.title',
    descriptionKey: 'analytics.app.description',
    icon: analyticsIcon,
    color: '#FB8C00',
    route: 'analytics',
    isOnMenu: true,
    showOnHomepage: true,
    requiredPermission: 'analytics.app.access',
  },
  {
    id: 'video-generator',
    titleKey: 'home.apps.items.videoGenerator.title',
    descriptionKey: 'home.apps.items.videoGenerator.description',
    icon: videoGeneratorIcon,
    color: '#6366F1',
    isOnMenu: false,
    // Hidden until the app ships: its routes are still disabled in the router,
    // so the homepage card advertised something nobody could open.
    showOnHomepage: false,
    disabled: true,
  },
]

/**
 * The user drawer's quick actions. No support shortcut: support ticketing is abandoned (the
 * owner's decision), and the public help centre is reached from the settings and account menus,
 * not from the rail.
 */
export const shellShortcuts: QuickAction[] = [
  // Debug shortcut only in dev mode (tree-shaken in production)
  ...(import.meta.env.DEV
    ? [
        {
          id: 'debug',
          icon: mdiBugOutline,
          label: 'Debug',
          tooltip: 'Debug Pages',
          route: 'debug',
        },
      ]
    : []),
]

/**
 * Whether an app's flag, if it names one, is on. `features` is the list `GET /api/app/init`
 * answers — the public flags enabled on the platform — so a flag absent from it is off.
 */
const isUnflagged = (app: App, features: readonly string[]) =>
  !app.requiredFeature || features.includes(app.requiredFeature)

/** The rail's entries whose flag is on. */
export const getAppsForMenuWithFeatures = (apps: App[], features: readonly string[]) =>
  apps.filter((app) => app.isOnMenu && isUnflagged(app, features))

/** The home page's cards whose flag is on. */
export const getAppsForHomepageWithFeatures = (apps: App[], features: readonly string[]) =>
  apps.filter((app) => app.showOnHomepage && isUnflagged(app, features))
